import {
  AUTH_REDIRECT_CODES,
  AuthState,
  bootHadPortfolioHandoff,
  useAuth,
} from "@/services/auth/auth";
import { getInitialPermissions } from "@/services/auth/permissions";
import { fetchData } from "@/services/graphql/queryUtil";
import {
  MarketplaceAgreementGateDocument,
  useAcceptMarketplaceTermsMutation,
  useMarketplaceAgreementGateQuery,
  type MarketplaceAgreementGateQuery,
  type MarketplaceAgreementGateQueryVariables,
  UserDataDocument,
  UserDataQuery,
  UserDataQueryVariables,
  useUserDataQuery,
} from "@/services/graphql/reactQuery";
import { DashboardLayout } from "@/shared/components/DashboardLayout";
import { MarketplaceAccessDeniedPage } from "@/shared/components/MarketplaceAccessDeniedPage";
import { NotFoundPage } from "@/shared/components/NotFoundPage";
import { UnauthorizedPage } from "@/shared/components/UnauthorizedPage";
import {
  ErrorComponentProps,
  MarketplaceAccessDeniedError,
  UnauthorizedError,
} from "@/shared/errors";
import {
  readActiveInstitutionId,
  readActivePortfolioId,
  reconcileActivePortfolioOwner,
} from "@/shared/hooks/useActivePortfolio";
import { useSidebarExpanded } from "@/shared/hooks/useSidebarExpanded";
import { useSidebarConfig } from "@/shared/paxSidebarConfig";
import { getJwtPayload, ServiceError } from "@ncino/pa-authentication-client-webclient";
import { PaxSidebar } from "@ncino/pax-ui";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, useMatchRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { isPendoInitialized, registerPendo } from "@/services/monitoring";
import { HouseLineIcon } from "@phosphor-icons/react";
import { AgreementGate } from "@/shared/components/AgreementGate/AgreementGate";
import { InstitutionChangedModal } from "@/shared/components/InstitutionChangedModal";
import { useIroncladWorkflow } from "@/services/ironclad/useIroncladWorkflow";

// Served from cache on same-session navigations (e.g. tab switches) instead of refetching;
// the agreement gate is explicitly invalidated after terms are accepted.
const ROOT_LOADER_STALE_TIME = 10 * 60 * 1000;

// The loader serves user data from cache; this background observer re-checks the
// user's institution so we can warn if it changed server-side mid-session.
const USERDATA_WATCH_INTERVAL = 10 * 60 * 1000;

export interface PaxContext {
  queryClient: QueryClient;
  auth: AuthState;
  institutionId: number;
  portfolioId: number;
  userName?: string;
  userFullName?: string;
}

export const Route = createRootRouteWithContext<PaxContext>()({
  component: RootComponent,
  errorComponent: (props: ErrorComponentProps) => {
    const { error } = props;
    if (error instanceof ServiceError && AUTH_REDIRECT_CODES.has(error.httpStatusCode ?? -1)) {
      return null;
    }
    if (error instanceof MarketplaceAccessDeniedError) {
      return <MarketplaceAccessDeniedPage {...props} />;
    }
    if (error instanceof UnauthorizedError) {
      return <UnauthorizedPage {...props} />;
    }
    throw error;
  },
  notFoundComponent: NotFoundPage,
  staticData: {
    breadcrumb: {
      title: ({ t }) => t("breadcrumbs.home"),
      icon: HouseLineIcon,
    },
  },
  beforeLoad: async ({ context }) => {
    let principalId = "";
    if (!context.auth.isAuthenticated) {
      try {
        const token = await context.auth.tokenProvider();
        principalId = getJwtPayload(token.token ?? "").sub;
      } catch (err) {
        if (err instanceof ServiceError && AUTH_REDIRECT_CODES.has(err.httpStatusCode ?? -1)) {
          return;
        }
        throw err;
      }
    }

    // Drop any active-portfolio override left by a previous user in this tab so a new
    // login resolves the portfolio from the user service. A PA handoff this boot seeded
    // the override for the incoming user, so it is claimed rather than cleared.
    const effectivePrincipalId = context.auth.principalId || principalId;
    if (effectivePrincipalId) {
      reconcileActivePortfolioOwner(effectivePrincipalId, bootHadPortfolioHandoff());
    }

    const userData = await context.queryClient.fetchQuery({
      queryKey: useUserDataQuery.getKey(),
      queryFn: () =>
        fetchData<UserDataQuery, UserDataQueryVariables>(
          UserDataDocument,
          context.auth.tokenProvider
        )(),
      staleTime: Infinity,
    });

    if (!userData.user_getUserData?.success) {
      throw new Error("Unable to fetch user data");
    }
    const institutionId = userData.user_getUserData?.institutionId;
    if (institutionId === null || institutionId === undefined) {
      throw new Error("Institution is undefined for user");
    }
    const activeInstitutionId = readActiveInstitutionId() ?? institutionId;
    const portfolioId = userData.user_getUserData?.portfolioData?.id;
    if (portfolioId === null || portfolioId === undefined) {
      throw new Error("Portfolio is undefined for user");
    }
    const userName = userData.user_getUserData?.firstName ?? undefined;
    const userFullName = `${userData.user_getUserData?.firstName} ${userData.user_getUserData?.lastName}`;

    const auth = {
      ...context.auth,
      principalId: context.auth.principalId ? context.auth.principalId : principalId,
    };
    await getInitialPermissions({
      queryClient: context.queryClient,
      auth,
      institutionId,
      portfolioId,
    });
    await context.queryClient.fetchQuery({
      queryKey: useMarketplaceAgreementGateQuery.getKey({
        institutionId: activeInstitutionId,
      }),
      queryFn: () =>
        fetchData<MarketplaceAgreementGateQuery, MarketplaceAgreementGateQueryVariables>(
          MarketplaceAgreementGateDocument,
          context.auth.tokenProvider
        )({ institutionId: activeInstitutionId }),
      staleTime: ROOT_LOADER_STALE_TIME,
    });
    if (!isPendoInitialized()) {
      registerPendo(effectivePrincipalId, readActivePortfolioId() ?? portfolioId);
    }

    return {
      institutionId: activeInstitutionId,
      portfolioId: readActivePortfolioId() ?? portfolioId,
      userName,
      userFullName,
    };
  },
});

// Background re-check of the user's institution. The loader context is fixed for the
// session, so a server-side institution change can leave the app in an inconsistent
// state; surface a blocking modal and let the user reload rather than silently switching.
function useInstitutionChanged(institutionId: number): boolean {
  const queryClient = useQueryClient();
  const { data } = useUserDataQuery(undefined, {
    refetchInterval: USERDATA_WATCH_INTERVAL,
    refetchOnWindowFocus: "always",
  });

  // refetchOnWindowFocus keys off tab visibility, so regaining window focus from another
  // app or browser instance (without a tab switch) does not trigger it; cover that here.
  useEffect(() => {
    const refetch = () => void queryClient.refetchQueries({ queryKey: useUserDataQuery.getKey() });
    window.addEventListener("focus", refetch);
    return () => window.removeEventListener("focus", refetch);
  }, [queryClient]);

  const liveInstitutionId = data?.user_getUserData?.institutionId;

  return liveInstitutionId != null && liveInstitutionId !== institutionId;
}

function RootComponent() {
  const matchRoute = useMatchRoute();
  const { isAuthenticated } = useAuth();
  const [expandedState, setExpandedState] = useSidebarExpanded();
  const sidebarConfig = useSidebarConfig(expandedState);
  const { institutionId } = Route.useRouteContext();
  const queryClient = useQueryClient();

  const institutionChanged = useInstitutionChanged(institutionId);

  const { data: agreementGate } = useMarketplaceAgreementGateQuery({ institutionId });
  const terms = agreementGate?.agreement_getMarketplaceGate?.termsToAccept;
  const accept = useAcceptMarketplaceTermsMutation();

  const { status: orderFormStatus, launch } = useIroncladWorkflow();

  const [dismissedVersion, setDismissedVersion] = useState<number | null>(null);

  const handleComplete = useCallback(() => {
    if (terms) setDismissedVersion(terms.version);
    void queryClient.invalidateQueries({
      queryKey: useMarketplaceAgreementGateQuery.getKey({ institutionId }),
    });
  }, [terms, queryClient, institutionId]);
  const handleSignNow = () => launch();

  if (!isAuthenticated) {
    return;
  }

  if (terms && terms.version !== dismissedVersion) {
    return (
      <AgreementGate
        key={terms.version}
        terms={terms}
        orderFormStatus={orderFormStatus}
        onAccept={(version) => accept.mutateAsync({ institutionId, version })}
        onComplete={handleComplete}
        onSignNow={handleSignNow}
        isAccepting={accept.isPending}
        error={accept.isError}
      />
    );
  }

  return (
    <>
      <InstitutionChangedModal open={institutionChanged} />
      <PaxSidebar
        isExpanded={expandedState}
        onExpandedChange={(value) => setExpandedState(value)}
        productDetails={sidebarConfig.AppDetails}
        topNavItems={sidebarConfig.topButtons}
        bottomNavItems={sidebarConfig.botButtons}
        drawerWidth={sidebarConfig.drawerWidth}
        isActive={(link) => !!matchRoute({ to: link, fuzzy: true })}
      >
        <DashboardLayout>
          <Outlet />
        </DashboardLayout>
      </PaxSidebar>
    </>
  );
}
